import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './ChatBot.css';
import RichMessageContent from './components/RichMessageContent';
import { FaCopy, FaPaperPlane, FaFilePdf, FaFileWord, FaFilePowerpoint, FaFileExcel, FaFileAlt, FaFileImage, FaFileVideo, FaFileAudio, FaFileArchive, FaFileCode, FaFile, FaGlobe, FaBuilding, FaClipboardList, FaStop } from 'react-icons/fa';
import { MdOpenInNew, MdClose, MdRefresh, MdInfo, MdAutorenew, MdLanguage } from "react-icons/md";
import { SiJavascript, SiTypescript, SiCss3, SiJson, SiPython, SiCplusplus } from 'react-icons/si';
import gpt_engine from './services/gpt_engine';
import { useEnvironment } from './contexts/EnvironmentContext';
import showdown from 'showdown';
import default_messages from './data/messages';
import { PENDING_MESSAGE_KEY, PENDING_AUTO_SEND_KEY } from './widgetTypes';

interface ChatbotProps {
  onClose: () => void;
  scope: string;
  search_level: string;
  env: string;
  initialMessage?: string | null;
  autoSend?: boolean;
}

export interface ChatBotHandle {
  setInput: (text: string) => void;
  sendMessage: (text?: string) => void;
}

interface Message {
  role: 'user' | 'system' | 'assistant';
  content: string;
  references?: string[];
}

const Chatbot = forwardRef<ChatBotHandle, ChatbotProps>(({ onClose, scope, search_level, env, initialMessage, autoSend = false }, ref) => {
  const { getLoginUrl, config, getMaiaHeaderLogoUrl, getMaiaIconUrl } = useEnvironment();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState<string>('');
  const inputRef = useRef(input);
  inputRef.current = input;
  const autoSendDoneRef = useRef(false);
  const sendMessageWithTextRef = useRef<(messageText: string) => Promise<void>>(async () => {});

  useImperativeHandle(ref, () => ({
    setInput: (text: string) => setInput(text),
    sendMessage: (text?: string) => {
      sendMessageWithTextRef.current(text ?? inputRef.current);
    },
  }));

  useEffect(() => {
    if (initialMessage && !autoSend) {
      setInput(initialMessage);
    }
  }, [initialMessage, autoSend]);

  useEffect(() => {
    const pendingMessage = sessionStorage.getItem(PENDING_MESSAGE_KEY);
    const pendingAutoSend = sessionStorage.getItem(PENDING_AUTO_SEND_KEY) === 'true';
    if (pendingMessage) {
      if (!pendingAutoSend) {
        setInput(pendingMessage);
      }
      sessionStorage.removeItem(PENDING_MESSAGE_KEY);
      sessionStorage.removeItem(PENDING_AUTO_SEND_KEY);
    }
  }, []);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [canSend, setCanSend] = useState<boolean>(true);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(true);
  const [currentThinkingMessage, setCurrentThinkingMessage] = useState<string>('');
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const [showGlobeConfirmModal, setShowGlobeConfirmModal] = useState<boolean>(false);
  const [showRefreshConfirmModal, setShowRefreshConfirmModal] = useState<boolean>(false);
  const [isGlobal, setIsGlobal] = useState<boolean>(false);
  const [regeneratingIndex, setRegeneratingIndex] = useState<number | null>(null);
  const [showDropdown, setShowDropdown] = useState<number | null>(null);
  const [showCopyDropdown, setShowCopyDropdown] = useState<number | null>(null);
  const [messagesWithGlobalPrompt, setMessagesWithGlobalPrompt] = useState<Set<number>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [isSharepoint, setIsSharepoint] = useState<boolean>(false);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [showReferencesModal, setShowReferencesModal] = useState<boolean>(false);
  const [selectedMessageReferences, setSelectedMessageReferences] = useState<string[]>([]);
  const [currentMessageReferences, setCurrentMessageReferences] = useState<string[]>([]);
  const [siteID, setSiteID] = useState<string>('');
  const [messages_variable, setMessageVariable] = useState<string>("chatbot-messages");
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  // Function to generate welcome message based on context
  const generateWelcomeMessage = (username: string): string => {
    const scopeWelcomeMessages = {
      cloudonedoc: {
        greeting: `Hello ${username || 'User'}, Welcome to maia for CloudOne Docs!`,
        description: "Ask me to find documentation, configurations, best practices, and technical guidance from our CloudOne knowledge base.",
        searchScope: ""
      },
      sechelp: {
        greeting: `Hello ${username || 'User'}, Welcome to maia for Security Policies!`,
        description: "Ask me to help you navigate security policies, compliance requirements, and security best practices from our knowledge base.",
        searchScope: ""
      },
      gethelp: {
        greeting: `Hello ${username || 'User'}, Welcome to maia for GTM Help!`,
        description: "Ask me to help you find 'How to Docs' and resources from the GTM Help Portal.",
        searchScope: ""
      },
      competebot: {
        greeting: `Hello ${username || 'User'}, Welcome to maia for Competitive Intelligence!`,
        description: "Ask me to find competitor information, market insights, and competitive positioning data from our knowledge base.",
        searchScope: ""
      },
      maiawiki: {
        greeting: `Hello ${username || 'User'}, Welcome to maia for maia Wiki!`,
        description: "Ask me to find specific information, configurations, and guidance from the maia Wiki knowledge base.",
        searchScope: ""
      },
      sharepointlite: {
        greeting: `Hello ${username || 'User'}, Welcome to maia for SharePoint!`,
        description: "Ask me to find documents, pages, and lists across this site. I'll surface only what you have access to.",
        searchScope: ""
      },
      sharepointliteall: {
        greeting: `Hello ${username || 'User'}, Welcome to maia for SharePoint!`,
        description: "Ask me to search across all SharePoint sites you have access to. I'll surface only what you can access.",
        searchScope: ""
      },
      default: {
        greeting: `Hello ${username || 'User'}, Welcome to maia!`,
        description: "Ask me anything — I can search through document repositories to find the information you need.",
        searchScope: ""
      }
    };

    const currentScope = config.scope as keyof typeof scopeWelcomeMessages;
    const welcomeData = scopeWelcomeMessages[currentScope] || scopeWelcomeMessages.default;

    return `${welcomeData.greeting}\n\n${welcomeData.description}`;
  };

  // Function to get bot information based on scope
  const getBotInfo = () => {
    const scopeInfo = {
      cloudonedoc: {
        title: "maia for CloudOne Docs",
        description: "I'm an AI assistant that searches through CloudOne documentation repositories. I can help you find specific information, configurations, and guidance from the CloudOne knowledge base.",
        link: "https://maiawiki.netapp.com/docs/en/maia-features/maiawidgets"
      },
      sechelp: {
        title: "maia for Security Policies",
        description: "I'm an AI assistant that searches through NetApp security policy and compliance documentation. I can help you find information about security requirements, policies, and standards from our security knowledge base.",
        link: "https://maiawiki.netapp.com/docs/en/maia-features/maiawidgets"
      },
      gethelp: {
        title: "maia for GTM Help",
        description: "I'm an AI assistant that searches through Go-To-Market documentation repositories. I can help you find sales materials, competitive information, and GTM resources from our knowledge base.",
        link: "https://maiawiki.netapp.com/docs/en/maia-features/maiawidgets"
      },
      competebot: {
        title: "maia for Competitive Intelligence",
        description: "I'm an AI assistant that searches through competitive intelligence and market analysis documentation. I can help you find competitor information and market insights from our knowledge base.",
        link: "https://maiawiki.netapp.com/docs/en/maia-features/maiawidgets"
      },
      maiawiki : {
        title: "maia for maia Wiki",
        description: "I'm an AI assistant that searches through the maia Wiki documentation repository. I can help you find specific information, configurations, and guidance from the maia Wiki knowledge base.",
        link: "https://maiawiki.netapp.com/docs/en"
      },
      sharepointlite: {
        title: "maia for SharePoint",
        description: "I'm an AI assistant that helps you find content within SharePoint sites. I can search through pages, documents, lists, and all other resources on any SharePoint site that you have access to.",
        link: "https://maiawiki.netapp.com/docs/en/maia-features/maia-for-sharepoint"
      },
      sharepointliteall: {
        title: "maia for SharePoint",
        description: "I'm a comprehensive SharePoint assistant that searches across all NetApp SharePoint sites you have access to. I can help you find documents, resources, and information from our entire SharePoint ecosystem.",
        link: "https://maiawiki.netapp.com/docs/en/maia-features/maia-for-sharepoint"
      },
      default: {
        title: "maia Assistant",
        description: "I'm an AI assistant that can help you answer any of your queries.",
        link: "https://maiawiki.netapp.com/docs/en/maia-features/maiawidgets"
      }
    };

    return scopeInfo[config.scope as keyof typeof scopeInfo] || scopeInfo.default;
  };

  const getInputPlaceholder = (): string => {
    if (scope === 'sharepointlite' || scope === 'sharepointliteall') {
      return 'Ask maia anything about this site…';
    }
    return 'Ask maia anything…';
  };

  const thinkingMessages = [
    'Searching for relevant files...',
    'Gathering the data...',
    'Processing your request...',
    'Preparing the response...'
  ];

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const startThinkingAnimation = () => {
    let messageIndex = 0;
    setCurrentThinkingMessage(thinkingMessages[0]);
    
    const interval = setInterval(() => {
      messageIndex = (messageIndex + 1) % thinkingMessages.length;
      setCurrentThinkingMessage(thinkingMessages[messageIndex]);
    }, 2500);
    
    return interval;
  };

  useEffect(() => {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    setMessages([{ role: 'system', content: 'Logging you in...' }]);
    setIsLoggingIn(true);
    
    const currentSite = window.location.href.toLowerCase();
    if (currentSite.includes('netapp.sharepoint.com')){
      setIsSharepoint(true);
    }

    const timer = setTimeout(() => {
      const savedMessages = localStorage.getItem(messages_variable);
      const welcomeMessage = generateWelcomeMessage(userDetails.username);
      
      if (savedMessages) {
        try {
          const parsedMessages = JSON.parse(savedMessages);
          if (parsedMessages.length > 1 || (parsedMessages.length === 1 && parsedMessages[0].content !== 'Logging you in...')) {
            setMessages(parsedMessages);
          } else {
            setMessages([{ 
              role: 'system', 
              content: welcomeMessage
            }]);
          }
        } catch (error) {
          console.error('Error parsing saved messages:', error);
          setMessages([{ 
              role: 'system', 
              content: welcomeMessage
          }]);
        }
      } else {
        setMessages([{ 
          role: 'system', 
          content: welcomeMessage
        }]);
      }
      setIsLoggingIn(false);
    }, 1500);

    const handleBeforeUnload = () => {
      localStorage.removeItem(messages_variable);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Replace the existing useEffects (lines 130-189) with these corrected ones:

useEffect(() => {
  const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
  setMessages([{ role: 'system', content: 'Logging you in...' }]);
  setIsLoggingIn(true);
  
  const currentSite = window.location.href.toLowerCase();
  if (currentSite.includes('netapp.sharepoint.com')){
    setIsSharepoint(true);
  }

  // Site detection and messages variable setup
  const currentSiteUrl = window.location.href;
  const url = new URL(currentSiteUrl);
  const pathParts = url.pathname.split('/').filter(part => part);
  const protocolAndDomain = `${url.protocol}//${url.hostname}`;
  const sitesIndex = pathParts.indexOf('sites');
  
  let currentSiteID = '';
  if (sitesIndex !== -1 && sitesIndex + 1 < pathParts.length) {
    const siteName = pathParts[sitesIndex + 1];
    currentSiteID = `${protocolAndDomain}/sites/${siteName}`;
    console.log('NetApp SharePoint site detected, added site-id header:', currentSiteID);
  } else {
    currentSiteID = protocolAndDomain;
    console.log('NetApp SharePoint domain detected, added site-id header:', protocolAndDomain);
  }
  
  setSiteID(currentSiteID);
  
  // Set the correct messages variable immediately
  const newMessagesVariable = currentSiteID ? `${currentSiteID}-${userDetails.username}-messages` : `${userDetails.username}-messages`;
  setMessageVariable(newMessagesVariable);

  const timer = setTimeout(() => {
    // Load saved messages for this specific site
    const savedMessages = localStorage.getItem(newMessagesVariable);
    const welcomeMessage = generateWelcomeMessage(userDetails.username);
    
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages);
        if (parsedMessages.length > 1 || (parsedMessages.length === 1 && parsedMessages[0].content !== 'Logging you in...')) {
          setMessages(parsedMessages);
        } else {
          setMessages([{ 
            role: 'system', 
            content: welcomeMessage
          }]);
        }
      } catch (error) {
        console.error('Error parsing saved messages:', error);
        setMessages([{ 
            role: 'system', 
            content: welcomeMessage
        }]);
      }
    } else {
      setMessages([{ 
        role: 'system', 
        content: welcomeMessage
      }]);
    }
    setIsLoggingIn(false);
  }, 1500);

  return () => {
    clearTimeout(timer);
  };
}, []); // Empty dependency array - only run once on mount

// Separate useEffect for saving messages
useEffect(() => {
  if (!isLoggingIn && messages.length > 0 && messages_variable) {
    const messagesToSave = messages.filter(msg => msg.content !== 'Logging you in...');
    if (messagesToSave.length > 0) {
      localStorage.setItem(messages_variable, JSON.stringify(messagesToSave));
    }
  }
}, [messages, isLoggingIn, messages_variable]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isLoading) {
      scrollToBottom();
    }
  }, [isLoading]);

  useEffect(() => {
    if (messages.length > 0) {
      // Check all assistant messages that haven't been processed yet
      messages.forEach((message, index) => {
        // Only check assistant messages that are not loading, not being regenerated, and not already in the global prompt set
        if (message.role === 'assistant' && 
            !isLoading && !isStreaming &&
            regeneratingIndex === null &&
            !isGlobal && 
            scope === 'sharepointlite' &&
            !messagesWithGlobalPrompt.has(index) && // Don't re-process messages already in the set
            isContentNotFoundMessage(message.content, message.references)) {
          
          // Only add to prompt if this is the most recent assistant message (last in conversation)
          const isLastAssistantMessage = index === messages.length - 1 || 
            (index < messages.length - 1 && messages.slice(index + 1).every(msg => msg.role === 'user'));
          
          if (isLastAssistantMessage) {
            setMessagesWithGlobalPrompt(prev => new Set(prev).add(index));
          }
        }
      });
    }
  }, [messages, isLoading, regeneratingIndex, isGlobal, scope, isStreaming, messagesWithGlobalPrompt]);

  // Add effect to handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only close dropdown on actual clicks, not mouse movements
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        // Check if the click is on the regenerate button itself
        const regenerateButton = (event.target as Element)?.closest('.regenerate-btn');
        if (!regenerateButton) {
          setShowDropdown(null);
        }
      }
    };

    // Use 'click' instead of 'mousedown' for better UX
    if (showDropdown !== null) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown]);

  // Add effect to handle clicks outside copy dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only close dropdown on actual clicks, not mouse movements
      const target = event.target as Element;
      
      // Check if the click is on the copy button or its dropdown
      const copyButton = target?.closest('.copy-btn');
      const copyDropdown = target?.closest('.dropdown-menu');
      
      // If click is not on copy button or its dropdown, close it
      if (!copyButton && !copyDropdown) {
        setShowCopyDropdown(null);
      }
    };

    // Use 'click' instead of 'mousedown' for better UX
    if (showCopyDropdown !== null) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showCopyDropdown]);

  const handleRelogin = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    event.preventDefault();
    const pending = sessionStorage.getItem(PENDING_MESSAGE_KEY) || input.trim();
    if (pending) {
      sessionStorage.setItem(PENDING_MESSAGE_KEY, pending);
    }
    sessionStorage.removeItem(PENDING_AUTO_SEND_KEY);
    localStorage.removeItem('userDetails');
    localStorage.removeItem(messages_variable);
    onClose();
    setTimeout(() => {
      const chatFab = document.getElementById('chat-fab');
      if (chatFab) {
        chatFab.click();
      } else {
        window.open(getLoginUrl(), 'login', 'width=500,height=600,scrollbars=yes,resizable=yes');
      }
    }, 100);
  };

  const isSessionExpiredMessage = (content: string) => {
    return content.includes('Session Expired or User UnAuthorized') || content.includes('Session Expired or User Unauthorized');
  };

  const isContentNotFoundMessage = (content: string, references?: string[]) => {
    if (scope !== 'sharepointlite') {
      return false;
    }

    // Helper function to check if text contains URLs
    const hasUrls = (text: string): boolean => {
      const urlRegex = /https?:\/\/[^\s]+/gi;
      return urlRegex.test(text);
    };

    // Check if references array has any URLs
    const hasReferences = references && references.length > 0;

    // Check if content has URLs
    const contentHasUrls = hasUrls(content);

    const contentLength = content.length;
    // If there are no URLs in either content or references, consider it as content not found
    if (!contentHasUrls && !hasReferences && contentLength > 200) {
      return true;
    }

    // Original pattern matching for "not found" messages
    const notFoundPatterns = [
      "I'm sorry, but I couldn't find any relevant information in the provided SharePoint documents",
      "I couldn't find any relevant information in the provided SharePoint documents",
      "I could not find any relevant information in the provided SharePoint documents",
      "I'm sorry, but I couldn't find",
      "I couldn't find",
      "I could not find",
      "No relevant information found",
      "Unable to find relevant information",
      "I don't have access to information",
      "I apologize, but I don't have",
      "Unfortunately, I don't have",
      "I'm unable to find",
      "content could not be found",
      "information is not available",
      "no matching documents",
      "no relevant documents found"
    ];
    
    return notFoundPatterns.some(pattern => 
      content.toLowerCase().includes(pattern.toLowerCase())
    );
  };

  const isReloginErrorMessage = (content: string) => {
    return content.includes('Session Expired or User Unauthorized') || 
           content.includes('Access denied. You don\'t have permission to access this service');
  };

  const restorePendingMessageToInput = (message: string) => {
    sessionStorage.setItem(PENDING_MESSAGE_KEY, message);
    sessionStorage.removeItem(PENDING_AUTO_SEND_KEY);
    setInput(message);
  };

  const handleSessionExpiredDuringSend = (messageToRestore: string, errorContent: string) => {
    restorePendingMessageToInput(messageToRestore);
    setMessages(prevMessages => {
      const updated = [...prevMessages];
      if (updated.length > 0 && updated[updated.length - 1].role === 'assistant') {
        updated.pop();
      }
      if (
        updated.length > 0 &&
        updated[updated.length - 1].role === 'user' &&
        updated[updated.length - 1].content === messageToRestore
      ) {
        updated.pop();
      }
      updated.push({ role: 'system', content: errorContent, references: [] });
      return updated;
    });
  };

  const isRefreshChatErrorMessage = (content: string) => {
    return content.includes('Service not found. The chat service is temporarily unavailable') ||
           content.includes('Internal server error. The service is experiencing technical difficulties') ||
           content.includes('Service temporarily unavailable. Please refresh the chat') ||
           content.includes('Service is temporarily unavailable for maintenance') ||
           content.includes('Request timeout. The service is taking longer than expected') ||
           content.includes('Too many requests. Please wait a moment and try again') ||
           content.includes('Network connection error. Please check your internet connection') ||
           content.includes('Request was cancelled. Please refresh the chat') ||
           content.includes('An unexpected error occurred while connecting to the service') ||
           content.includes('Unexpected error occurred');
  };

  const handleRefreshChat = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    event.preventDefault();
    setShowRefreshConfirmModal(true);
  };

  const confirmRefreshChat = () => {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const welcomeMessage = generateWelcomeMessage(userDetails.username);
    setMessages([{ 
      role: 'system', 
      content: welcomeMessage
    }]);
    localStorage.removeItem(messages_variable);
    setShowRefreshConfirmModal(false);
  };

  const renderReloginErrorMessage = (content: string) => {
    const parts = content.split(': relogin link');
    return (
      <div>
        {parts.length > 1 ? (
          <div className="message-action-block">
            <p className="message-action-text">{parts[0]}</p>
            <div className="message-action-buttons">
              <button type="button" className="netai-action-btn netai-action-btn--primary" onClick={handleRelogin}>
                Relogin Link
              </button>
            </div>
          </div>
        ) : (
          parts[0]
        )}
      </div>
    );
  };

  function convertToHTML(content: string): string {
    const converter = new showdown.Converter();
    return converter.makeHtml(content);
  }

  function copyToClipboardAsHTML(htmlContent: string, plainText: string) {
    const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
    const textBlob = new Blob([plainText], { type: 'text/plain' });
    const clipboardItem = new ClipboardItem({ 
      'text/html': htmlBlob,
      'text/plain': textBlob
    });
    navigator.clipboard.write([clipboardItem]);
  }

  const handleCopy = (content: string, format: 'markdown' | 'richtext' | 'html', index:number) => {
    if (format === 'markdown') {
      navigator.clipboard.writeText(content);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1000);
    } else if (format === 'richtext') {
      const htmlContent = convertToHTML(content);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1000);
      copyToClipboardAsHTML(htmlContent, content);
    } else if (format === 'html') {
      const htmlContent = convertToHTML(content);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1000);
      copyToClipboardAsHTML(htmlContent, content);
    }
    // setCopied(true);
    // setTimeout(() => setCopied(false), 2000); // Reset copied state after 2 seconds
  };

  const renderRefreshChatErrorMessage = (content: string) => {
    const refreshChatIndex = content.indexOf(': refresh chat');

    if (refreshChatIndex !== -1) {
      const errorPart = content.substring(0, refreshChatIndex);
      const afterRefreshPart = content.substring(refreshChatIndex + ': refresh chat'.length);

      return (
        <div>
          <div className="message-action-block">
            <p className="message-action-text">
              {errorPart}
              {afterRefreshPart}
            </p>
            <div className="message-action-buttons">
              <button type="button" className="netai-action-btn netai-action-btn--primary" onClick={handleRefreshChat}>
                refresh chat
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        {content}
      </div>
    );
  };

  const renderSessionExpiredMessage = (content: string) => {
    const parts = content.split('relogin link');
    return (
      <div>
        <div className="message-action-block">
          <p className="message-action-text">{parts[0]}</p>
          <div className="message-action-buttons">
            <button type="button" className="netai-action-btn netai-action-btn--primary" onClick={handleRelogin}>
              Relogin Link
            </button>
          </div>
        </div>
        {parts[1] || ''}
      </div>
    );
  };

  const handleGlobalSearchRegenerate = async (messageIndex: number) => {
    if (!canSend || isLoading) return;

    let userMessageIndex = messageIndex - 1;
    while (userMessageIndex >= 0 && messages[userMessageIndex].role !== 'user') {
      userMessageIndex--;
    }

    if (userMessageIndex < 0) return;

    const contextMessages = messages.slice(0, userMessageIndex + 1);

    const originalIsGlobal = isGlobal;
    setIsGlobal(true);

    setRegeneratingIndex(messageIndex);
    setIsLoading(true);
    setIsStreaming(true);
    setCanSend(false);
    setCurrentMessageReferences([]);

    const thinkingInterval = startThinkingAnimation();

    try {
      const messagesToSend = contextMessages.filter(msg => msg.content !== 'Logging you in...');

      setMessages(prevMessages => {
        const updatedMessages = prevMessages.slice(0, messageIndex);
        updatedMessages.push({ role: 'assistant', content: '', references: [] });
        return updatedMessages;
      });

      setMessagesWithGlobalPrompt(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageIndex);
        return newSet;
      });

      let botMessage: Message = { role: 'assistant', content: '', references: [] };

      await gpt_engine(env, scope, true, search_level, messagesToSend, 
        (streamedContent: string) => {
          setIsLoading(false);
          setRegeneratingIndex(null);
          clearInterval(thinkingInterval);
          
          botMessage.content += streamedContent;
          setMessages(prevMessages => {
            const updatedMessages = [...prevMessages];
            updatedMessages[updatedMessages.length - 1] = { ...botMessage };
            return updatedMessages;
          });
          
          setTimeout(scrollToBottom, 10);
        },
        (references: string[]) => {
          botMessage.references = references;
          setCurrentMessageReferences(references);
          setMessages(prevMessages => {
            const updatedMessages = [...prevMessages];
            updatedMessages[updatedMessages.length - 1] = { ...botMessage };
            return updatedMessages;
          });
        }
      );
    } catch (error) {
      console.error('Error regenerating with global search:', error);
      clearInterval(thinkingInterval);
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages];
        if (updatedMessages.length > 0) {
          updatedMessages[updatedMessages.length - 1] = { 
            role: 'assistant',
            content: 'Something went wrong while searching additional SharePoint sites. Please try again later. (Make sure you are connected to VPN)',
            references: []
          };
        }
        return updatedMessages;
      });
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      setCanSend(true);
      setRegeneratingIndex(null);
      clearInterval(thinkingInterval);
      setIsGlobal(originalIsGlobal);
    }
  };

  const handleGlobalSearchDecline = (messageIndex: number) => {
    setMessagesWithGlobalPrompt(prev => {
      const newSet = new Set(prev);
      newSet.delete(messageIndex);
      return newSet;
    });
  };

  // Function to extract filename from URL
  const extractFilename = (url: string): string => {
    try {
      // Remove query parameters like ?web=1
      const cleanUrl = url.split('?')[0];
      // Split by '/' and get the last part
      const parts = cleanUrl.split('/');
      const filename = parts[parts.length - 1];
      
      // If no filename found, use domain name
      if (!filename || filename.length === 0) {
        const urlObj = new URL(url);
        return urlObj.hostname;
      }
      
      // Decode URI component to handle encoded characters
      const decodedFilename = decodeURIComponent(filename);
      
      // Remove file extension for display
      const lastDotIndex = decodedFilename.lastIndexOf('.');
      if (lastDotIndex > 0 && lastDotIndex < decodedFilename.length - 1) {
        return decodedFilename.substring(0, lastDotIndex);
      }
      
      return decodedFilename;
    } catch (error) {
      // Fallback: return the full URL if parsing fails
      return url;
    }
  };

  // Function to get file type icon based on file extension or URL pattern
  const getFileTypeIcon = (url: string): React.ReactElement => {
    try {
      // Extract full filename with extension for icon detection
      const cleanUrl = url.split('?')[0];
      const parts = cleanUrl.split('/');
      const fullFilename = parts[parts.length - 1];
      
      if (!fullFilename || fullFilename.length === 0) {
        return <FaGlobe style={{ color: '#2196f3', marginRight: '8px' }} />;
      }
      
      const decodedFilename = decodeURIComponent(fullFilename).toLowerCase();
      const extension = decodedFilename.split('.').pop();
      
      // File type specific icons - check extension from FULL filename
      switch (extension) {
        // Document files
        case 'pdf':
          return <FaFilePdf style={{ color: '#d32f2f', marginRight: '8px' }} />;
        case 'doc':
        case 'docx':
          return <FaFileWord style={{ color: '#1976d2', marginRight: '8px' }} />;
        case 'ppt':
        case 'pptx':
          return <FaFilePowerpoint style={{ color: '#d84315', marginRight: '8px' }} />;
        case 'xls':
        case 'xlsx':
          return <FaFileExcel style={{ color: '#388e3c', marginRight: '8px' }} />;
        case 'txt':
          return <FaFileAlt style={{ color: '#666666', marginRight: '8px' }} />;
        
        // Web files
        case 'html':
        case 'htm':
          return <FaGlobe style={{ color: '#ff9800', marginRight: '8px' }} />;
        case 'aspx':
          return <FaGlobe style={{ color: '#3f51b5', marginRight: '8px' }} />;
        
        // Image files
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'bmp':
        case 'svg':
          return <FaFileImage style={{ color: '#4caf50', marginRight: '8px' }} />;
        
        // Video files
        case 'mp4':
        case 'avi':
        case 'mov':
        case 'wmv':
          return <FaFileVideo style={{ color: '#e91e63', marginRight: '8px' }} />;
        
        // Audio files
        case 'mp3':
        case 'wav':
        case 'wma':
          return <FaFileAudio style={{ color: '#9c27b0', marginRight: '8px' }} />;
        
        // Archive files
        case 'zip':
        case 'rar':
        case '7z':
        case 'tar':
          return <FaFileArchive style={{ color: '#795548', marginRight: '8px' }} />;
        
        // Code files
        case 'js':
          return <SiJavascript style={{ color: '#f7df1e', marginRight: '8px' }} />;
        case 'ts':
          return <SiTypescript style={{ color: '#3178c6', marginRight: '8px' }} />;
        case 'jsx':
        case 'tsx':
          return <FaFileCode style={{ color: '#61dafb', marginRight: '8px' }} />;
        case 'css':
        case 'scss':
          return <SiCss3 style={{ color: '#1572b6', marginRight: '8px' }} />;
        case 'json':
          return <SiJson style={{ color: '#000000', marginRight: '8px' }} />;
        case 'xml':
          return <FaFileCode style={{ color: '#ff6f00', marginRight: '8px' }} />;
        case 'py':
          return <SiPython style={{ color: '#3776ab', marginRight: '8px' }} />;
       
        case 'cpp':
        case 'c':
          return <SiCplusplus style={{ color: '#00599c', marginRight: '8px' }} />;
        
        // Default cases - now check SharePoint patterns for files without extensions
        default:
          // Check for SharePoint-specific patterns only if no file extension was found
          if (url.includes('sharepoint.com')) {
            if (url.includes('/sites/') || url.includes('/SitePages/')) {
              return <FaBuilding style={{ color: '#0078d4', marginRight: '8px' }} />;
            }
            if (url.includes('/Lists/') || url.includes('/_layouts/')) {
              return <FaClipboardList style={{ color: '#0078d4', marginRight: '8px' }} />;
            }
          }
          
          // Check if it's a general web URL (no file extension)
          if (url.startsWith('http') && !decodedFilename.includes('.')) {
            return <FaGlobe style={{ color: '#2196f3', marginRight: '8px' }} />;
          }
          // Generic file icon for unknown types
          return <FaFile style={{ color: '#666666', marginRight: '8px' }} />;
      }
    } catch (error) {
      // Fallback icon
      return <FaFile style={{ color: '#666666', marginRight: '8px' }} />;
    }
  };

  const regenerateMessage = async (index: number) => {
    if (!canSend || isLoading) return;

    const messageToRegenerate = messages[index];
    
    if (messageToRegenerate.role === 'system') {
      return;
    }

    let contextMessages: Message[] = [];
    
    if (messageToRegenerate.role === 'assistant') {
      let userMessageIndex = index - 1;
      while (userMessageIndex >= 0 && messages[userMessageIndex].role !== 'user') {
        userMessageIndex--;
      }

      if (userMessageIndex < 0) return;

      contextMessages = messages.slice(0, userMessageIndex + 1);
    }

    setRegeneratingIndex(index);
    setIsLoading(true);
    setIsStreaming(true);
    setCanSend(false);
    setCurrentMessageReferences([]);

    const thinkingInterval = startThinkingAnimation();

    try {
      const messagesToSend = contextMessages.filter(msg => msg.content !== 'Logging you in...');

      setMessages(prevMessages => {
        const updatedMessages = prevMessages.slice(0, index);
        updatedMessages.push({ role: 'assistant', content: '', references: [] });
        return updatedMessages;
      });

      let botMessage: Message = { role: 'assistant', content: '', references: [] };

      await gpt_engine(env, scope, false, search_level, messagesToSend, 
        (streamedContent: string) => {
          setIsLoading(false);
          setRegeneratingIndex(null);
          clearInterval(thinkingInterval);
          
          botMessage.content += streamedContent;
          setMessages(prevMessages => {
            const updatedMessages = [...prevMessages];
            updatedMessages[updatedMessages.length - 1] = { ...botMessage };
            return updatedMessages;
          });
          
          setTimeout(scrollToBottom, 10);
        },
        (references: string[]) => {
          botMessage.references = references;
          setCurrentMessageReferences(references);
          setMessages(prevMessages => {
            const updatedMessages = [...prevMessages];
            updatedMessages[updatedMessages.length - 1] = { ...botMessage };
            return updatedMessages;
          });
        }
      );
    } catch (error) {
      console.error('Error regenerating message:', error);
      clearInterval(thinkingInterval);
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages];
        if (updatedMessages.length > 0) {
          updatedMessages[updatedMessages.length - 1] = { 
            role: 'assistant',
            content: 'Something went wrong while regenerating. Please try again later. (Make sure you are connected to VPN)',
            references: []
          };
        }
        return updatedMessages;
      });
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      setCanSend(true);
      setRegeneratingIndex(null);
      clearInterval(thinkingInterval);
    }
  };

  const newChat = () => {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const welcomeMessage = generateWelcomeMessage(userDetails.username);
    setMessages([{ 
      role: 'system', 
      content: welcomeMessage
    }]);
    localStorage.removeItem(messages_variable);
  }

  const handleGlobeClick = () => {
    setShowGlobeConfirmModal(true);
  };

  const renderContentNotFoundMessage = (content: string, messageIndex: number) => {
    // Only show prompt for the last message when streaming is complete
    const isLastMessage = messageIndex === messages.length - 1;
    const shouldShowPrompt = messagesWithGlobalPrompt.has(messageIndex) && 
                            !isGlobal && 
                            scope === 'sharepointlite' && 
                            isLastMessage && 
                            !isLoading && 
                            !isStreaming &&
                            regeneratingIndex === null;
    
    return (
      <div>
        <RichMessageContent content={content} markdownComponents={markdownComponents} />
        {shouldShowPrompt && (
          <div className="global-search-prompt">
            <p className="global-search-prompt-text">
              Do you want to search additional SharePoint sites you have access to?
            </p>
            <div className="global-search-prompt-actions">
              <button
                type="button"
                className="netai-action-btn netai-action-btn--primary"
                onClick={() => handleGlobalSearchRegenerate(messageIndex)}
              >
                Yes
              </button>
              <button
                type="button"
                className="netai-action-btn netai-action-btn--secondary"
                onClick={() => handleGlobalSearchDecline(messageIndex)}
              >
                No
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const markdownComponents = {
    a: ({ href, children, ...props }: any) => {
      const isAlreadyEncoded = /%[0-9A-Fa-f]{2}/.test(href);
      const encodedHref = isAlreadyEncoded ? href : encodeURI(href);
      return (
        <a href={encodedHref} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      );
    },
    table: ({ children, ...props }: any) => (
      <div className="markdown-table-container">
        <table className="markdown-table" {...props}>
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }: any) => (
      <thead className="markdown-thead" {...props}>
        {children}
      </thead>
    ),
    tbody: ({ children, ...props }: any) => (
      <tbody className="markdown-tbody" {...props}>
        {children}
      </tbody>
    ),
    tr: ({ children, ...props }: any) => (
      <tr className="markdown-tr" {...props}>
        {children}
      </tr>
    ),
    th: ({ children, ...props }: any) => (
      <th className="markdown-th" {...props}>
        {children}
      </th>
    ),
    td: ({ children, ...props }: any) => (
      <td className="markdown-td" {...props}>
        {children}
      </td>
    )
  };

  // Add effect to handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only close dropdown on actual clicks, not mouse movements
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        // Check if the click is on the regenerate button itself
        const regenerateButton = (event.target as Element)?.closest('.regenerate-btn');
        if (!regenerateButton) {
          setShowDropdown(null);
        }
      }
    };

    // Use 'click' instead of 'mousedown' for better UX
    if (showDropdown !== null) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown]);

  // Function to position dropdown below the regenerate button
  const positionDropdown = (buttonElement: HTMLElement, dropdownElement: HTMLElement) => {
    const buttonRect = buttonElement.getBoundingClientRect();
    const dropdownRect = dropdownElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Calculate position below the button
    let top = buttonRect.bottom + 4; // 4px gap below button
    let left = buttonRect.right - dropdownRect.width; // Align right edge with button

    // Adjust if dropdown would go off screen vertically
    if (top + dropdownRect.height > viewportHeight - 10) {
      top = buttonRect.top - dropdownRect.height - 4; // Position above button instead
    }

    // Adjust if dropdown would go off screen horizontally
    if (left < 10) {
      left = 10; // Minimum left margin
    } else if (left + dropdownRect.width > viewportWidth - 10) {
      left = viewportWidth - dropdownRect.width - 10; // Maximum right position
    }

    dropdownElement.style.top = `${top}px`;
    dropdownElement.style.left = `${left}px`;
  };

  // Update dropdown positioning when it opens
  useEffect(() => {
    if (showDropdown !== null) {
      const timer = setTimeout(() => {
        const dropdownElement = document.querySelector('.dropdown-menu') as HTMLElement;
        const regenerateButton = document.querySelector(`[data-message-index="${showDropdown}"] .regenerate-btn`) as HTMLElement;
        
        if (dropdownElement && regenerateButton) {
          positionDropdown(regenerateButton, dropdownElement);
        }
      }, 10); // Small delay to ensure DOM is updated

      return () => clearTimeout(timer);
    }
  }, [showDropdown]);

  // Function to stop the current streaming request
  const stopStreaming = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setIsLoading(false);
      setIsStreaming(false);
      setCanSend(true);
      
      // Update the last message to indicate it was stopped
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages];
        if (updatedMessages.length > 0) {
          const lastMessage = updatedMessages[updatedMessages.length - 1];
          if (lastMessage.role === 'assistant' && lastMessage.content === '') {
            // Remove empty assistant message if it was just created
            updatedMessages.pop();
          } else if (lastMessage.role === 'assistant') {
            // Add stopped indicator to partial message
            lastMessage.content += '\n\n*[Response stopped by user]*';
          }
        }
        return updatedMessages;
      });
    }
  };

  const sendMessageWithText = async (messageText: string) => {
    if (messageText.trim() === '' || !canSend || isLoading) return;
    const userMessage: Message = { role: 'user', content: messageText.trim() };

    setMessages(prevMessages => [...prevMessages, userMessage]);
    const messageToSend = messageText.trim();
    setInput('');

    setIsLoading(true);
    setIsStreaming(true);
    setCanSend(false);
    setCurrentMessageReferences([]);

    const controller = new AbortController();
    setAbortController(controller);

    const thinkingInterval = startThinkingAnimation();

    try {
      const savedMessages = localStorage.getItem(messages_variable);
      const saved_messages = savedMessages ? JSON.parse(savedMessages) : [];
      saved_messages.push(userMessage);

      let botMessage: Message = { role: 'assistant', content: '', references: [] };
      setMessages(prevMessages => [...prevMessages, botMessage]);

      await gpt_engine(env, scope, isGlobal, search_level, saved_messages, 
        (streamedContent: string) => {
          if (controller.signal.aborted) {
            return;
          }
          
          setIsLoading(false);
          clearInterval(thinkingInterval);
          botMessage.content += streamedContent;
          setMessages(prevMessages => {
            const updatedMessages = [...prevMessages];
            updatedMessages[updatedMessages.length - 1] = { ...botMessage };
            return updatedMessages;
          });
          
          setTimeout(scrollToBottom, 10);
        },
        (references: string[]) => {
          if (controller.signal.aborted) {
            return;
          }
          
          botMessage.references = references;
          setCurrentMessageReferences(references);
          setMessages(prevMessages => {
            const updatedMessages = [...prevMessages];
            updatedMessages[updatedMessages.length - 1] = { ...botMessage };
            return updatedMessages;
          });
        },
        controller.signal
      );

      const lastMsg = botMessage.content;
      if (isSessionExpiredMessage(lastMsg) || isReloginErrorMessage(lastMsg)) {
        handleSessionExpiredDuringSend(messageToSend, lastMsg);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Request was aborted');
        return;
      }
      
      console.error('Error sending message:', error);
      clearInterval(thinkingInterval);
      const errorMessage = error instanceof Error ? error.message : 'Something went wrong. Please try again later. (Make sure you are connected to VPN)';
      
      if (errorMessage.includes('Session Expired') || errorMessage.includes('relogin')) {
        handleSessionExpiredDuringSend(messageToSend, errorMessage);
      } else {
        setMessages(prevMessages => [...prevMessages, { role: 'system', content: errorMessage, references: [] }]);
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
        setIsStreaming(false);
        setCanSend(true);
        setAbortController(null);
      }
      clearInterval(thinkingInterval);
    }
  };

  sendMessageWithTextRef.current = sendMessageWithText;

  useEffect(() => {
    autoSendDoneRef.current = false;
  }, [autoSend, initialMessage]);

  useEffect(() => {
    if (!autoSend || !initialMessage || isLoggingIn || !canSend || autoSendDoneRef.current) {
      return;
    }

    autoSendDoneRef.current = true;
    sendMessageWithText(initialMessage);
  }, [autoSend, initialMessage, isLoggingIn, canSend]);

  const sendMessage = async () => {
    await sendMessageWithText(input);
  };

  const openChat = () => {
    const message = default_messages.filter(msg => scope === msg.scope)[0]
    const url = message ? message.linkUrl : '/';
    let final_url;
    if (env === 'DEV') {
      final_url = "https://maia-dev.netapp.com" + url;
    } else if (env === 'STG') {
      final_url = "https://maia-stg.netapp.com" + url;
    } else if (env === 'PRD') {
      final_url = "https://maia.netapp.com" + url;
    } else if (env === 'LOCAL') {
      final_url = "https://maia-dev.netapp.com" + url;
    } else {
      // Default to prod if env is unrecognized
      final_url = "https://maia.netapp.com" + url;
    }
    window.open(final_url, '_blank');
  };

  const copyMessage = async (message: string, index: number) => {
    try {
      await navigator.clipboard.writeText(message);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1000);
    } catch (err) {
      console.error('Failed to copy message:', err);
    }
  };

  return (
    <div className="chatbot-container">
      <div className="maia-hairline" aria-hidden="true" />
      <div className="chatbot-header">
        <div className="header-content">
          <div className="chatbot-branding">
            <img
              src={getMaiaHeaderLogoUrl()}
              alt="maia AI"
              className="maia-header-logo"
            />
          </div>
          <div className="header-actions">
            <button onClick={openChat} className="header-btn" title="Open maia Chat">
              <MdOpenInNew />
            </button>
            <button onClick={() => setShowInfoModal(true)} className="header-btn" title="Info">
              <MdInfo />
            </button>
            <button onClick={onClose} className="header-btn close-btn" title="Close">
              <MdClose />
            </button>
          </div>
        </div>
      </div>
      
      <div className="chatbot-messages">
        {messages.map((msg, index) => (
          msg.role === 'user' ? (
            <div key={index} className={`message-wrapper ${msg.role}`}>
              <div className="message-row user">
                <div className={`chatbot-message ${msg.role}`}>
                  <div className="user-message-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{msg.content}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div key={index} className={`message-wrapper ${msg.role}`}>
              <div className={`message-row ${msg.role}`}>
                <div className="bot-avatar">
                  <img src={getMaiaIconUrl()} alt="" />
                </div>
                <div className="bot-bubble-group">
                  <div className={`chatbot-message ${msg.role}`}>
                    <div className="bot-message-content">
                      {(isLoading && index === messages.length - 1 && regeneratingIndex === null) || (regeneratingIndex === index) ? (
                        <div className="loading-indicator">
                          <div className="loading-dots" aria-hidden="true">
                            <span />
                            <span />
                            <span />
                          </div>
                          <span className="loading-text">{currentThinkingMessage}</span>
                        </div>
                      ) : (
                        isSessionExpiredMessage(msg.content) ? (
                          renderSessionExpiredMessage(msg.content)
                        ) : (
                          isReloginErrorMessage(msg.content) ? (
                            renderReloginErrorMessage(msg.content)
                          ) : (
                            isRefreshChatErrorMessage(msg.content) ? (
                              renderRefreshChatErrorMessage(msg.content)
                            ) : (
                              (isContentNotFoundMessage(msg.content, msg.references) && index === messages.length - 1 && msg.role === 'assistant' && !isLoading && !isStreaming && regeneratingIndex === null) ? (
                                renderContentNotFoundMessage(msg.content, index)
                              ) : (
                                <RichMessageContent content={msg.content} markdownComponents={markdownComponents} />
                              )
                            )
                          )
                        )
                      )}
                    </div>
                    {!isLoading && !(isStreaming && index === messages.length - 1) && ((msg.role === 'assistant') || (msg.role === 'system' && index !== 0)) && (
                      <div className="bot-message-footer" ref={showDropdown === index ? dropdownRef : null}>
                        <div className="bot-footer-disclaimer">
                          Verify the AI-generated response carefully.
                        </div>
                        <div className="bot-footer-actions">
                          <button
                            className="regenerate-btn"
                            data-message-index={index}
                            onClick={() => {
                              if (scope === 'sharepointlite' && isSharepoint) {
                                setShowDropdown(showDropdown === index ? null : index);
                              } else {
                                regenerateMessage(index);
                              }
                            }}
                            title="Regenerate Message"
                            disabled={isLoading || regeneratingIndex === index}
                          >
                            {regeneratingIndex === index ? (
                              <div className="loading-dots" aria-hidden="true">
                                <span />
                                <span />
                                <span />
                              </div>
                            ) : (
                              <MdAutorenew />
                            )}
                          </button>
                          {showDropdown === index && (scope === 'sharepointlite') && (isSharepoint) && (
                            <div className="dropdown-menu">
                              <button
                                className="dropdown-item"
                                onClick={() => {
                                  setShowDropdown(null);
                                  regenerateMessage(index);
                                }}
                              >
                                Current Site
                              </button>
                              <button
                                className="dropdown-item"
                                onClick={() => {
                                  setShowDropdown(null);
                                  handleGlobalSearchRegenerate(index);
                                }}
                              >
                                All SharePoint Sites
                              </button>
                            </div>
                          )}
                          <button
                            className="copy-btn"
                            onClick={() => {
                              setShowCopyDropdown(showCopyDropdown === index ? null : index);
                            }}
                            title="Copy Message"
                          >
                            {copiedIndex === index ? 'Copied!' : <FaCopy />}
                          </button>
                          {showCopyDropdown === index && (
                            <div className="dropdown-menu">
                              <button
                                className="dropdown-item"
                                onClick={() => {
                                  setShowCopyDropdown(null);
                                  handleCopy(msg.content, 'markdown', index);
                                }}
                              >
                                Raw Markdown
                              </button>
                              <button
                                className="dropdown-item"
                                onClick={() => {
                                  setShowCopyDropdown(null);
                                  handleCopy(msg.content, 'richtext', index);
                                }}
                              >
                                HTML Rich Text
                              </button>
                            </div>
                          )}
                          {msg.references && msg.references.length > 0 && (
                            <button
                              className="references-btn"
                              onClick={() => {
                                setSelectedMessageReferences(msg.references || []);
                                setShowReferencesModal(true);
                              }}
                              title="View Sources"
                            >
                              <MdLanguage />
                              <span>Sources</span>
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="chatbot-input">
        <div className="input-row">
          <button 
            onClick={handleRefreshChat} 
            className="refresh-chat-btn" 
            title="Delete this chat and Start New"
            disabled={!canSend}
          >
            <MdRefresh />
          </button>
          <div className="input-container">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && canSend && sendMessage()}
              placeholder={getInputPlaceholder()}
              disabled={!canSend}
            />
            {isStreaming ? (
              <button 
                onClick={stopStreaming}
                className="stop-btn"
                title="Stop Generating Response"
              >
                <FaStop />
              </button>
            ) : (
              <button 
                disabled={!canSend || input.trim() === ''} 
                onClick={sendMessage}
                className="send-btn"
                title="Send Message"
              >
                <FaPaperPlane />
              </button>
            )}
          </div>
        </div>
        {(scope === 'sharepointlite' || scope === 'sharepointliteall') && (
          <div className="input-disclaimer">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect width="18" height="11" x="3" y="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Search results are limited to content you can access
          </div>
        )}
      </div>

      {showInfoModal && (
  <div className="info-modal">
    <div className="info-modal-content">
      <h2>{getBotInfo().title}</h2>
      <p>{getBotInfo().description}</p>
      <p className="info-modal-link">
        For more information, visit <a href={getBotInfo().link} target="_blank" rel="noopener noreferrer">maia Wiki</a>
      </p>
      <p>
      For support or to provide feedback, please contact <br /><a href="mailto:ng-it-genai-coe@netapp.com">ng-it-genai-coe@netapp.com</a>
      </p>
      <button onClick={() => setShowInfoModal(false)} className="close-info-modal-btn">Close</button>
    </div>
  </div>
)}

{showGlobeConfirmModal && (
  <div className="confirm-modal">
    <div className="confirm-modal-content">
      <h2>Confirm Action</h2>
      <p>This feature allows searching across all SharePoint sites you have access to, rather than just the current site. <br/> Would you like to {isGlobal ? 'disable' : 'enable'} Global SharePoint Search?</p>
    </div>
  </div>
)}

{showReferencesModal && (
        <div className="references-modal">
          <div className="references-modal-content">
            <div className="references-modal-header">
              <h2>Sources</h2>
              <button 
                onClick={() => setShowReferencesModal(false)} 
                className="close-references-modal-btn"
                title="Close"
              >
                <MdClose />
              </button>
            </div>
            <div className="references-modal-body">
              <div className="references-disclaimer">
                The resources below were provided to AI for constructing this response.
              </div>
              {selectedMessageReferences.length > 0 ? (
                <ul className="references-list">
                  {selectedMessageReferences.map((url, index) => (
                    <li key={index} className="reference-item">
                      <a 
                        href={url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="reference-link"
                        title={url}
                      >
                        {getFileTypeIcon(url)} {extractFilename(url)}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No references available for this message.</p>
              )}
            </div>
          </div>
        </div>
      )}

{showRefreshConfirmModal && (
  <div className="confirm-modal">
    <div className="confirm-modal-content">
      <h2>Confirm Action</h2>
      <p>Are you sure you want to delete this chat and start a new one?</p>
      <div className="confirm-modal-actions">
        <button 
          onClick={confirmRefreshChat} 
          className="confirm-btn"
        >
          Yes
        </button>
        <button 
          onClick={() => setShowRefreshConfirmModal(false)} 
          className="cancel-btn"
        >
          No
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
});

export default Chatbot;
